




















import Vue from 'vue'
import Icon from '@/components/Icon/Icon.vue'
import Minimap from '@/components/Minimap/Minimap.vue'
import TabsPanel from '@/components/Common/TabsPanel.vue'
import { localStorageGet, localStorageSet } from '@/helpers/localStorage'
import { isDevelopHost } from '@/constants/enviroment'

import type { Point, Position } from '@/@types'

interface Data {
  isGrab: boolean
  position: Position
  scrollPoint: Point
  zoom: number
  isDevelopHost: boolean
}

interface Methods {
  saveScroll: () => void
  changeZoom: (scaleUp: boolean) => void
  handleMouseMove: (e: MouseEvent) => void
  handleMouseUp: () => void
  handleMouseDown: (e: MouseEvent) => void
  handleMouseWheel: (e: WheelEvent) => void
}

interface Computed {
  scrollElement: HTMLDivElement
  contentElement: HTMLDivElement
  actualUrl: string
  zoomLabel: string
  zoomValue: number
}

interface Props {
  dataUpdate: number
}

const ZOOM_STEP = 0.5
const MAX_ZOOM_RATIO = 5

export default Vue.extend<Data, Methods, Computed, Props>({
  destroyed: function() {
    this.scrollElement.removeEventListener('scroll', this.saveScroll)
    this.scrollElement.removeEventListener('mousedown', this.handleMouseDown)
    this.scrollElement.removeEventListener('wheel', this.handleMouseWheel)
  },
  mounted: function() {
    const scrollJson: string = localStorageGet(this.actualUrl, true)

    if (scrollJson) {
      const { x, y }: Point = JSON.parse(scrollJson)

      this.scrollElement.scrollTo({
        left: x,
        top: y,
        behavior: 'smooth',
      })
    }

    this.scrollElement.addEventListener('scroll', this.saveScroll)
    this.scrollElement.addEventListener('mousedown', this.handleMouseDown)
    this.scrollElement.addEventListener('wheel', this.handleMouseWheel)
  },
  components: {
    Icon,
    Minimap,
    TabsPanel,
  },
  props: {
    dataUpdate: {
      default: () => 0,
    },
  },
  data() {
    return {
      isGrab: false,
      position: { top: 0, left: 0, x: 0, y: 0 },
      scrollPoint: { x: 0, y: 0 },
      zoom: 0,
      isDevelopHost,
    }
  },
  methods: {
    saveScroll() {
      this.scrollPoint = {
        x: this.scrollElement.scrollLeft,
        y: this.scrollElement.scrollTop,
      }
      localStorageSet(this.actualUrl, JSON.stringify(this.scrollPoint))
    },
    changeZoom(scaleUp: boolean) {
      const newZoomValue = this.zoom + (scaleUp ? ZOOM_STEP : -ZOOM_STEP)
      if (newZoomValue > MAX_ZOOM_RATIO) {
        return (this.zoom = MAX_ZOOM_RATIO)
      }
      if (newZoomValue < -MAX_ZOOM_RATIO) {
        return (this.zoom = -MAX_ZOOM_RATIO)
      }
      this.zoom = newZoomValue
    },
    handleMouseMove(e: MouseEvent) {
      const dx = e.clientX - this.position.x
      const dy = e.clientY - this.position.y
      this.scrollElement.scrollTop = this.position.top - dy
      this.scrollElement.scrollLeft = this.position.left - dx
    },
    handleMouseUp() {
      this.isGrab = false

      this.scrollElement.removeEventListener('mousemove', this.handleMouseMove)
      this.scrollElement.removeEventListener('mouseup', this.handleMouseUp)
    },
    handleMouseDown(e: MouseEvent) {
      const element = e.target as Element
      const elementTag = element.tagName.toLowerCase()

      if (['a', 'i'].includes(elementTag)) {
        return
      }

      this.isGrab = true
      this.position = {
        left: this.scrollElement.scrollLeft,
        top: this.scrollElement.scrollTop,
        x: e.clientX,
        y: e.clientY,
      }

      this.scrollElement.addEventListener('mousemove', this.handleMouseMove)
      this.scrollElement.addEventListener('mouseup', this.handleMouseUp)
    },
    handleMouseWheel(e: WheelEvent) {
      e.preventDefault()
      this.changeZoom(e.deltaY > 0)
    },
  },
  computed: {
    scrollElement(): HTMLDivElement {
      return this.$refs.scrollContainer as HTMLDivElement
    },
    contentElement(): HTMLDivElement {
      return this.$refs.contentContainer as HTMLDivElement
    },
    actualUrl(): string {
      return window.location.toString()
    },
    zoomLabel(): string {
      return `${100 + this.zoom * 10}%`
    },
    zoomValue(): number {
      return 1 + this.zoom * 0.1
    }
  },
  watch: {
    zoom() {
      this.$emit('on-change-zoom', this.zoomValue)
      this.contentElement.style.transform = `scale(${this.zoomValue})`
    },
  },
})
